import React, { useState, useEffect } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getProfilePlaceholder } from '../../utils/placeholders';
import notificationService from '../../services/notificationService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const ContentPreApproval = () => {
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchDrafts = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Query contentDrafts collection — fetch all, filter/sort client-side
        const draftsRef = collection(db, 'contentDrafts');
        let draftsQuery = query(draftsRef, where('brandId', '==', currentUser.uid));
        
        let draftsSnapshot;
        try {
          draftsQuery = query(draftsQuery, orderBy('submittedAt', 'desc'));
          draftsSnapshot = await getDocs(draftsQuery);
        } catch (indexError) {
          console.warn('Sorting failed (index may not exist), fetching without sorting:', indexError);
          draftsQuery = query(draftsRef, where('brandId', '==', currentUser.uid));
          draftsSnapshot = await getDocs(draftsQuery);
        }
        
        const draftsData = [];
        
        // Process each draft document
        for (const docSnapshot of draftsSnapshot.docs) {
          const draftData = docSnapshot.data();
          
          // Get campaign information
          let campaignData = {};
          if (draftData.campaignId) {
            try {
              const campaignRef = doc(db, 'campaigns', draftData.campaignId);
              const campaignSnap = await getDoc(campaignRef);
              if (campaignSnap.exists()) {
                campaignData = campaignSnap.data();
              }
            } catch (error) {
              console.error('Error fetching campaign:', error);
            }
          }
          
          // Get influencer information
          let influencerData = {};
          if (draftData.influencerId) {
            try {
              const influencerRef = doc(db, 'users', draftData.influencerId);
              const influencerSnap = await getDoc(influencerRef);
              if (influencerSnap.exists()) {
                influencerData = influencerSnap.data();
              }
            } catch (error) {
              console.error('Error fetching influencer:', error);
            }
          }
          
          // Format submission data
          const submissionData = {
            id: docSnapshot.id,
            ...draftData,
            campaign: {
              id: draftData.campaignId || '',
              name: campaignData.name || draftData.campaignTitle || 'Unknown Campaign',
              title: campaignData.name || draftData.campaignTitle || 'Unknown Campaign',
              requiredHashtags: campaignData.requiredHashtags || [],
              requiredMentions: campaignData.requiredMentions || ''
            },
            influencer: {
              id: draftData.influencerId || '',
              name: influencerData.displayName || draftData.influencerName || 'Unknown Influencer',
              email: influencerData.email || draftData.influencerEmail || '',
              profileImage: influencerData.profileImageUrl || influencerData.photoURL || draftData.influencerProfileImage || getProfilePlaceholder(),
              followers: influencerData.socialMedia?.instagram?.followers || 0
            },
            submittedAt: draftData.submittedAt || draftData.resubmittedAt,
            isResubmission: !!draftData.resubmittedAt
          };
          
          draftsData.push(submissionData);
        }
        
        setDrafts(draftsData);
      } catch (error) {
        console.error('Error fetching content drafts:', error);
        setError('Failed to load content drafts. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDrafts();
  }, [currentUser]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  const handleApproveDraft = async (draftId) => {
    try {
      await handleAsyncOperation(async () => {
        const draft = drafts.find(d => d.id === draftId);
        if (!draft) throw new Error('Draft not found');

        // Update draft status
        const draftRef = doc(db, 'contentDrafts', draftId);
        await updateDoc(draftRef, {
          status: 'approved',
          brandFeedback: feedbackText || 'Approved for posting',
          approvedAt: new Date()
        });

        // Update campaign participant status
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', draft.campaignId),
          where('influencerId', '==', draft.influencerId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const participantId = querySnapshot.docs[0].id;
          const participantRef = doc(db, 'campaignParticipants', participantId);
          await updateDoc(participantRef, {
            draftStatus: 'approved',
            draftApprovedAt: new Date()
          });
        }

        // Send notification to influencer
        await notificationService.createNotification(
          draft.influencerId,
          'draft_approved',
          'Content Draft Approved!',
          `Your content draft for "${draft.campaign.name}" has been approved. You can now post your content and submit final proof.`,
          {
            relatedId: draftId,
            relatedType: 'contentDraft',
            campaignId: draft.campaignId
          }
        );

        // Update local state
        setDrafts(prev => prev.map(d => 
          d.id === draftId 
            ? { ...d, status: 'approved', brandFeedback: feedbackText || 'Approved for posting' }
            : d
        ));

        setFeedbackText('');
        setShowModal(false);
        setSelectedDraft(null);
      });

      showSuccess('Content draft approved successfully!', 'The influencer has been notified and can now post their content.', 4000);
    } catch (error) {
      console.error('Error approving draft:', error);
      showError('Failed to approve content draft.', 'Please try again.', 5000);
    }
  };

  const handleRejectDraft = async (draftId) => {
    if (!feedbackText.trim()) {
      showError('Feedback is required when rejecting a draft.', 'Please provide specific feedback for the influencer.', 5000);
      return;
    }

    try {
      await handleAsyncOperation(async () => {
        const draft = drafts.find(d => d.id === draftId);
        if (!draft) throw new Error('Draft not found');

        // Update draft status
        const draftRef = doc(db, 'contentDrafts', draftId);
        await updateDoc(draftRef, {
          status: 'rejected',
          brandFeedback: feedbackText,
          rejectedAt: new Date()
        });

        // Update campaign participant status
        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', draft.campaignId),
          where('influencerId', '==', draft.influencerId)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const participantId = querySnapshot.docs[0].id;
          const participantRef = doc(db, 'campaignParticipants', participantId);
          await updateDoc(participantRef, {
            draftStatus: 'revision_requested',
            draftFeedback: feedbackText,
            draftRevisionRequestedAt: new Date()
          });
        }

        // Send notification to influencer
        await notificationService.createNotification(
          draft.influencerId,
          'draft_rejected',
          'Content Draft Needs Revision',
          `Your content draft for "${draft.campaign.name}" needs revision. Please check the feedback and resubmit.`,
          {
            relatedId: draftId,
            relatedType: 'contentDraft',
            campaignId: draft.campaignId
          }
        );

        // Update local state
        setDrafts(prev => prev.map(d => 
          d.id === draftId 
            ? { ...d, status: 'rejected', brandFeedback: feedbackText }
            : d
        ));

        setFeedbackText('');
        setShowModal(false);
        setSelectedDraft(null);
      });

      showSuccess('Revision requested successfully!', 'The influencer has been notified and can resubmit their draft.', 4000);
    } catch (error) {
      console.error('Error rejecting draft:', error);
      showError('Failed to request revision.', 'Please try again.', 5000);
    }
  };

  const openModal = (draft, action) => {
    setSelectedDraft({ ...draft, action });
    setFeedbackText('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDraft(null);
    setFeedbackText('');
  };

  const getStatusBadge = (status, isResubmission = false) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center space-x-2">
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              Pending Review
            </span>
            {isResubmission && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Resubmission
              </span>
            )}
          </div>
        );
      case 'approved':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            Revision Requested
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const filteredDrafts = drafts
    .filter(draft => {
      if (filter === 'all') return true;
      return draft.status === filter;
    })
    .sort((a, b) => {
      const aTime = a.submittedAt?.toDate?.() || new Date(a.submittedAt || 0);
      const bTime = b.submittedAt?.toDate?.() || new Date(b.submittedAt || 0);
      return sortBy === 'oldest' ? aTime - bTime : bTime - aTime;
    });

  // Pagination
  const totalPages = Math.ceil(filteredDrafts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDrafts = filteredDrafts.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pb-12">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Content Pre-Approval</h2>
        <p className="text-gray-600">Review and approve content drafts before they go live</p>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {['all', 'pending', 'approved', 'rejected'].map((filterOption) => (
                <button
                  key={filterOption}
                  onClick={() => handleFilterChange(filterOption)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    filter === filterOption
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterOption === 'all' ? 'All Drafts' : 
                   filterOption === 'pending' ? 'Pending Review' :
                   filterOption === 'approved' ? 'Approved' : 'Needs Revision'}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Sort by:</label>
              <Select
                value={sortBy}
                onValueChange={(value) => handleSortChange(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content Drafts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {paginatedDrafts.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Content Drafts</h3>
            <p className="text-gray-600">
              {filter === 'all'
                ? 'No content drafts have been submitted yet.'
                : `No ${filter} content drafts found.`}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Influencer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedDrafts.map((draft) => (
                    <tr key={draft.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={draft.influencer.profileImage}
                              alt={draft.influencer.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = getProfilePlaceholder();
                              }}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {draft.influencer.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {draft.influencer.followers > 0 && `${draft.influencer.followers.toLocaleString()} followers`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {draft.campaign.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(draft.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(draft.status, draft.isResubmission)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <Link
                          to={`/brand/content-pre-approval/${draft.id}`}
                          className="text-primary hover:text-primary-dark"
                        >
                          Review
                        </Link>
                        {draft.status === 'pending' && (
                          <>
                            <button
                              onClick={() => openModal(draft, 'approve')}
                              className="text-green-600 hover:text-green-900 ml-2"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => openModal(draft, 'reject')}
                              className="text-red-600 hover:text-red-900 ml-2"
                            >
                              Request Revision
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, filteredDrafts.length)}</span> of{' '}
                        <span className="font-medium">{filteredDrafts.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(totalPages)].map((_, index) => {
                          const page = index + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-primary border-primary text-white'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Approval/Rejection Modal */}
      {showModal && selectedDraft && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedDraft.action === 'approve' ? 'Approve Content Draft' : 'Request Revision'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  {selectedDraft.action === 'approve' 
                    ? 'Approve this content draft to allow the influencer to post their content.'
                    : 'Provide specific feedback for the influencer to improve their content draft.'}
                </p>
                
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={selectedDraft.action === 'approve' 
                    ? 'Optional feedback for the influencer...'
                    : 'Please provide specific feedback about what needs to be changed...'}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  rows={4}
                  required={selectedDraft.action === 'reject'}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => selectedDraft.action === 'approve' 
                    ? handleApproveDraft(selectedDraft.id)
                    : handleRejectDraft(selectedDraft.id)}
                  className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
                    selectedDraft.action === 'approve'
                      ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {selectedDraft.action === 'approve' ? 'Approve Draft' : 'Request Revision'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPreApproval;
